import { Link } from 'react-router-dom';
import {
  AddressChip,
  Badge,
  Card,
  Delta,
  FloorRail,
  PhaseBadge,
  Price,
  Progress,
  Skeleton,
  Sparkline,
  TokenMark,
} from '@/components/ui';
import { useMarket } from '@/providers/market';
import { cn } from '@/lib/cn';
import { compact, eth, priceSub, share, timeAgo, weiToNumber } from '@/lib/format';
import type { DemoToken, TokenPhase } from '@/lib/demo/types';

/** Sparkline height. The skeleton mirrors it with `h-11` (44px) — keep in sync. */
const SPARK_H = 44;

const clamp01 = (n: number) => (Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0);

/**
 * ETH figure for the card's number strip, capped at five characters.
 *
 * `eth()`'s own precision yields six characters across the 10–999 band
 * (`90.095`), which needs 41px in a 40px cell — so cards clipped intermittently
 * as the live tick walked values over the boundary. Three decimals are noise on
 * a grid tile anyway. One decimal below 1000 and none above it: a flat
 * `digits: 1` would push the thousands band the other way (`6,456.8`).
 * The `<0.1` guard stops a small non-zero figure rounding to a flat "0".
 */
function stripEth(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (value > 0 && abs < 0.05) return '<0.1';
  // From 10k up, go through compact() with no decimals. A decimal there breaks
  // the cap at every band edge, where rounding adds an integer digit: 99_999
  // renders "100.0K" at one decimal, but "100K" at none.
  if (abs >= 10_000) return compact(value, 0);
  return eth(value, { digits: abs >= 1000 ? 0 : 1, unit: false });
}

/**
 * Phases the card footer cannot express, and that a buyer needs *before* they
 * click: `launching` means the anti-snipe window is live and the fee is still
 * elevated, `halted` means trading is stopped (redeem at floor stays open).
 *
 * The other three are already stated at the bottom of the card — "To graduation
 * 97%" with a bar, or a Graduated badge with buyback and burn counts — so a
 * header chip repeating them only costs the name its width.
 */
const WARNING_PHASES: readonly TokenPhase[] = ['launching', 'halted'];

/**
 * One column of the card's number strip.
 *
 * The unit rides the value as a small muted suffix rather than sitting in the
 * eyebrow: a 7-character caps eyebrow is all that fits at `tracking-[0.14em]`
 * once the card bottoms out near 280px (the `xl` breakpoint, where the sidebar
 * is widest relative to the grid). The suffix is `shrink-0` so the number
 * truncates before the unit does — the unit is the part that can't be inferred.
 */
function StatCell({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div className="min-w-0 px-2 py-2">
      <div className="truncate text-3xs uppercase tracking-[0.14em] text-subtle">{label}</div>
      <div className="mt-0.5 flex min-w-0 items-baseline gap-1">
        <span className="min-w-0 truncate font-mono text-2xs tabular-nums text-fg">{value}</span>
        {unit ? <span className="shrink-0 text-3xs text-subtle">{unit}</span> : null}
      </div>
    </div>
  );
}

export function TokenCard({ token }: { token: DemoToken }) {
  const { getSparkline } = useMarket();

  const spot = weiToNumber(token.spotPrice);
  const floorPrice = weiToNumber(token.floorPrice);
  const ratio = clamp01(token.floorRatio);
  const progress = clamp01(token.graduationProgress);
  const warning = WARNING_PHASES.includes(token.phase);

  return (
    <Link
      to={`/token/${token.address}`}
      aria-label={`${token.symbol} — ${token.name}`}
      className="group block h-full rounded-xl"
    >
      <Card interactive className="flex h-full flex-col gap-3">
        {/* 1 ── identity + price.
                Line 1 is symbol + age + price, line 2 is the name with the full
                row (a warning chip takes the head of it when there is one).

                Age sits beside the symbol rather than in the footer because the
                footer measured too tight at 1280px: 30.9px free on a trading
                card and 2.0px on a graduated one, against ~36px for "· 24m".
                Line 1 has 74.7px free next to a six-character symbol, so the
                age lands here for free — and on a launching card it ends up
                next to the price, which is where it is actually actionable.

                The "Launching" chip (86px) still does not fit on line 1; the
                age is 20px, which is a different question. ────────────────── */}
        <div className="flex items-start gap-3">
          <TokenMark symbol={token.symbol} hue={token.hue} glyph={token.glyph} size="md" />

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 items-baseline gap-1.5">
                {/* The symbol never gives way; the age is the only thing here
                    that may, and it is three characters at most. */}
                <span className="shrink-0 font-mono text-sm font-semibold uppercase leading-tight text-fg">
                  {token.symbol}
                </span>
                <span className="shrink-0 text-2xs text-subtle" aria-hidden="true">
                  ·
                </span>
                <span className="min-w-0 truncate font-mono text-2xs text-muted">
                  {timeAgo(token.createdAt)}
                </span>
              </div>

              <div className="shrink-0 text-right">
                {/* `block` so the Delta drops to its own line instead of flowing beside it. */}
                <Price value={spot} className="block text-sm font-medium leading-tight text-fg" />
                <Delta value={token.change24h} className="mt-0.5 text-2xs" />
              </div>
            </div>

            {/* Fixed height so a card carrying a chip is no taller than one without. */}
            <div className="mt-1 flex h-5 items-center gap-1.5 text-2xs text-muted">
              {warning ? <PhaseBadge phase={token.phase} /> : null}
              <span className="min-w-0 truncate">{token.name}</span>
            </div>
          </div>
        </div>

        {/* 2 ── 24h track. `floorRatio` draws the guaranteed band as the bottom
                slice of the box, so the price shape stays readable. ───────── */}
        <Sparkline
          data={getSparkline(token.address)}
          floorRatio={ratio}
          tone="auto"
          area
          height={SPARK_H}
          className="w-full"
        />

        {/* 3 ── the signature metric ─────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-3xs uppercase tracking-[0.14em] text-subtle">Floor coverage</span>
            <Badge variant="floor" size="sm">
              {share(ratio)}
            </Badge>
          </div>
          <FloorRail
            className="mt-1.5"
            floorRatio={ratio}
            height={8}
            showLabels
            floorLabel={priceSub(floorPrice)}
            spotLabel={`Spot ${priceSub(spot)}`}
          />
        </div>

        {/* 4 ── three-up numbers ─────────────────────────────────────────── */}
        {/* Holders carries the shortest value, so the two ETH columns take the
            slack — but not all of it. Every eyebrow is 7 caps characters, so the
            binding constraint is the *narrowest* column's label, not the values.
            Worst case is the xl breakpoint (1280px viewport → 280px card → 244px
            of strip), where 0.92fr leaves both the ETH figures and "HOLDERS"
            about 4px of margin even on a pessimistic reading of Inter's caps. */}
        <div className="grid grid-cols-[1fr_1fr_0.92fr] divide-x divide-line rounded-lg border border-line bg-elevated/50">
          <StatCell label="24h vol" value={stripEth(token.volume24hEth)} unit="ETH" />
          <StatCell label="Mkt cap" value={stripEth(token.marketCapEth)} unit="ETH" />
          <StatCell label="Holders" value={compact(token.holderCount, 0)} />
        </div>

        {/* 5 ── lifecycle + creator ──────────────────────────────────────── */}
        <div className="mt-auto flex min-h-9 items-center justify-between gap-3 pt-0.5">
          <div className="min-w-0 flex-1">
            {token.graduated ? (
              <div className="flex min-w-0 flex-col gap-1">
                <div className="flex items-center gap-1.5">
                  <Badge variant="up" size="sm">
                    Graduated
                  </Badge>
                  <span className="shrink-0 whitespace-nowrap font-mono text-3xs text-subtle">
                    {compact(token.buybacks, 0)}× buyback
                  </span>
                </div>
                <span className="whitespace-nowrap font-mono text-3xs text-muted">
                  {eth(token.ethBurnedEth)} burned
                </span>
              </div>
            ) : (
              <Progress value={progress} tone="bull" size="sm" label="To graduation" showValue />
            )}
          </div>
          <span className="shrink-0">
            <AddressChip
              address={token.creator}
              handle={token.creatorHandle}
              copy={false}
              link={false}
              className="text-2xs"
            />
          </span>
        </div>
      </Card>
    </Link>
  );
}

/** Geometry-matched placeholder — same slots, same heights, no layout shift. */
export function TokenCardSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn('flex h-full flex-col gap-3', className)}>
      <div className="flex items-start gap-3">
        <Skeleton variant="circle" className="h-9 w-9 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <Skeleton variant="text" className="h-4 w-28" />
            <div className="w-20 shrink-0 space-y-1.5">
              <Skeleton variant="text" className="h-4 w-full" />
              <Skeleton variant="text" className="ml-auto h-3 w-2/3" />
            </div>
          </div>
          <Skeleton variant="text" className="mt-1 h-5 w-3/5" />
        </div>
      </div>

      <Skeleton variant="block" className="h-11 w-full" />

      <div>
        <div className="flex items-center justify-between gap-2">
          <Skeleton variant="text" className="h-3 w-24" />
          <Skeleton variant="text" className="h-4 w-12" />
        </div>
        <Skeleton variant="block" className="mt-1.5 h-2 w-full rounded-full" />
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <Skeleton variant="text" className="h-3 w-20" />
          <Skeleton variant="text" className="h-3 w-20" />
        </div>
      </div>

      <div className="grid grid-cols-[1fr_1fr_0.92fr] divide-x divide-line rounded-lg border border-line bg-elevated/50">
        {[0, 1, 2].map((i) => (
          <div key={i} className="min-w-0 px-2 py-2">
            <Skeleton variant="text" className="h-3 w-3/4" />
            <Skeleton variant="text" className="mt-1 h-3 w-1/2" />
          </div>
        ))}
      </div>

      <div className="mt-auto flex min-h-9 items-center justify-between gap-3 pt-0.5">
        <Skeleton variant="text" className="h-4 w-2/5" />
        <Skeleton variant="text" className="h-4 w-20" />
      </div>
    </Card>
  );
}
